import React, { useState } from 'react';
import {
  Button,
  Col,
  ControlLabel,
  FormGroup,
  HelpBlock,
  Row,
} from 'react-bootstrap';
import { Field } from 'react-final-form';
import { capitalize } from 'lodash';
import { validateEditorData } from '../../editor/defaultOptions.js';
import FieldGroup from '../../FieldGroup.jsx';
import { InfoModal } from '../../modals/InfoModal.jsx';
import ChangeImpactTypeSelect from './ChangeImpactFormFields/ChangeImpactTypeSelect.jsx';
import ScaleField from './ChangeImpactFormFields/ScaleField.jsx';
import PerceptionField from './ChangeImpactFormFields/PerceptionField.jsx';
import ActionPlanStatusField from './ChangeImpactFormFields/ActionPlanStatusField.jsx';
import CheckboxAdapter from '../../CheckboxAdapter.jsx';
import DatePickerAdapter from '../../DatePickerAdapter.jsx';
import { ChangeImpactsLink } from '../../links/index.jsx';
import { INITIATIVE_TYPES } from '../../../lib/schema.js';
import StakeholdersSelect from '../../stakeholders-select/StakeholdersSelect.jsx';
import CurrentCSVDownloadButton from '../Stakeholders/CurrentCSVDownloadButton.jsx';
import StakeholdersExampleCSVButton from '../../StakeholdersExampleCSVButton.jsx';
import { getOrgSlug } from '../../../lib/utils.js';
import EmployeesFormField from '../../employees-form-field/EmployeesFormField.jsx';
import AuthorizedAction from '../../AuthorizedAction.js';
import WhoIsImpactedSelect from './WhoIsImpactedSelect.jsx';
import ImpactedExampleCSVButton from './ImpactedExampleCSVButton.jsx';
import ImpactedCurrentCSVDownloadButton from './ImpactedCurrentCSVDownloadButton.jsx';

import Editor from '../../editor/editor.jsx';

function ChangeImpactForm(props) {
  const {
    values,
    formRef,
    editing,
    handleSubmit,
    onSelectOwner,
    onUpdateExternal,
    onShowManageParticipantsModal,
    ungroupedSearchParams,
    onSelectUngroupedEmployee,
    onSelectAllGroupEmployees,
    onSelectGroup,
    onShowManageGroupModal,
    onShowGroupModal,
    onShowEmployeeModal,
    projectId,
    organization,
    changeImpactTypes,
    setCurrentStateEditorInstance,
    setFutureStateEditorInstance,
    setActionPlanEditorInstance,
    setUploadStakeholdersMethod,
    setReplaceStakeholdersMethod,
    uploadStakeholdersMethod,
    replaceStakeholdersMethod,
    setUploadImpactedMethod,
    setReplaceImpactedMethod,
    uploadImpactedMethod,
    replaceImpactedMethod,
    setCreateCITypeModal,
    orgStakeholders,
    submitFailed,
    submitting,
    submitError,
    onDownloadCsv,
    participantsEditAllowed,
    entities,
    updateEntities,
  } = props;
  const {
    externalEntities,
    ungroupedEmployees,
    groups,
    owners,
    groupStakeholders,
    individuals,
  } = entities;
  const [showLimitInfoModal, setShowLimitInfoModal] = useState(false);

  return (
    <form ref={formRef} onSubmit={handleSubmit}>
      <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
        <h3>General</h3>
      </div>

      <Row>
        <Col xs={8}>
          <Field
            id="title"
            name="title"
            label="Change Impact Title"
            placeholder="Enter Title"
            type="text"
            component={FieldGroup}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={8}>
          <div className="m-b-24">
            <Field
              id="owner"
              name="owner"
              selected={owners}
              handleSelect={onSelectOwner}
              title="Change Impact Owner"
              isItemActive={(val) => owners.some(({ id }) => id === val)}
              addBtn={
                <AuthorizedAction action="create" subject="employee">
                  <button
                    type="button"
                    className="btn-link"
                    onClick={onShowEmployeeModal}
                  >
                    {' '}
                    + Add People
                  </button>
                </AuthorizedAction>
              }
              component={EmployeesFormField}
            />
          </div>
        </Col>
        <Col xs={8}></Col>
      </Row>
      <Row>
        <Col xs={8}>
          <Field
            id="peopleImpacted"
            name="people_impacted"
            label="Number of People Impacted"
            placeholder="Enter Number"
            type="number"
            component={FieldGroup}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={8}>
          <Row>
            <Col xs={6}>
              <Field
                id="startDate"
                name="start_date"
                label="Start Date"
                placeholderText="Select a Date"
                component={DatePickerAdapter}
                allowNull
              />
            </Col>
            <Col xs={6}>
              <Field
                id="endDate"
                name="end_date"
                label="End Date"
                placeholderText="Select a Date"
                component={DatePickerAdapter}
                allowNull
              />
            </Col>
          </Row>
        </Col>
      </Row>

      {participantsEditAllowed ? (
        <React.Fragment>
          <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
            <h3>Who is Impacted</h3>
          </div>

          <Row>
            <Col xs={8}>
              <WhoIsImpactedSelect
                editing={editing}
                externalEntities={externalEntities}
                ungroupedEmployees={ungroupedEmployees}
                groups={groups}
                onUpdateExternal={onUpdateExternal}
                onShowManageParticipantsModal={onShowManageParticipantsModal}
                ungroupedSearchParams={ungroupedSearchParams}
                onSelectUngroupedEmployee={onSelectUngroupedEmployee}
                onSelectAllGroupEmployees={onSelectAllGroupEmployees}
                onSelectGroup={onSelectGroup}
                onShowManageGroupModal={onShowManageGroupModal}
                onShowGroupModal={onShowGroupModal}
                uploadMethod={uploadImpactedMethod}
                replaceMethod={replaceImpactedMethod}
                setUploadMethod={setUploadImpactedMethod}
                setReplaceMethod={setReplaceImpactedMethod}
                exampleCSVDownloadButton={<ImpactedExampleCSVButton />}
                currentCSVDownloadButton={
                  <ImpactedCurrentCSVDownloadButton
                    onDownloadCsv={() => onDownloadCsv('impacted')}
                  />
                }
              />
            </Col>
          </Row>
          <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
            <h3>Add Stakeholders</h3>
          </div>

          <Row>
            <Col xs={8}>
              <StakeholdersSelect
                editing={editing}
                orgStakeholders={orgStakeholders}
                selectedGroupStakeholders={groupStakeholders}
                updateSelectedGroupStakeholders={(val) =>
                  updateEntities({ groupStakeholders: val })
                }
                updateSelectedIndividuals={(val) =>
                  updateEntities({ individuals: val })
                }
                selectedIndividuals={individuals}
                uploadMethod={uploadStakeholdersMethod}
                replaceMethod={replaceStakeholdersMethod}
                setUploadMethod={setUploadStakeholdersMethod}
                setReplaceMethod={setReplaceStakeholdersMethod}
                exampleCSVDownloadButton={<StakeholdersExampleCSVButton />}
                currentCSVDownloadButton={
                  <CurrentCSVDownloadButton
                    onDownloadCsv={() => onDownloadCsv('stakeholders')}
                  />
                }
              />
            </Col>
          </Row>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
            <h3>Participants</h3>
          </div>
          <p className="text-muted m-b-48">
            It’s not allowed to change participants in outdated change impacts.
            If you want to proceed, please correct change impact dates first.
          </p>
        </React.Fragment>
      )}

      <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
        <h3>Settings</h3>
      </div>
      <Row>
        <Col xs={8}>
          <ChangeImpactTypeSelect
            changeImpactTypes={changeImpactTypes}
            data={values}
          />
          {organization?.canManageCustomSettings ? (
            <div
              className="flex m-b-38"
              style={{ justifyContent: 'space-between' }}
            >
              <button
                type="button"
                className="btn-link"
                onClick={(e) => {
                  e.preventDefault();
                  if (
                    changeImpactTypes.filter((ciType) => !ciType.archived)
                      .length < 15
                  ) {
                    setCreateCITypeModal(true);
                  } else {
                    setShowLimitInfoModal(true);
                  }
                }}
              >
                {' '}
                + Create New Change Impact Type
              </button>
              <button
                type="button"
                className="btn-link"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(
                    `/${getOrgSlug()}/organization/custom-settings/change-impact-types/edit`,
                    '_blank'
                  );
                }}
              >
                {' '}
                Manage Change Impact Types
              </button>
            </div>
          ) : null}
        </Col>
      </Row>

      <Row>
        <Col xs={4}>
          <ScaleField label="Scale" name="scale" />
        </Col>
        <Col xs={4}>
          <PerceptionField label="Perception" name="perception" />
        </Col>
      </Row>

      <Row>
        <Col xs={4}>
          <FormGroup>
            <ControlLabel>Action Plan Type</ControlLabel>
            {INITIATIVE_TYPES.map((type) => (
              <Field
                key={`actionPlanType${capitalize(type)}`}
                id={`action_plan_type_${capitalize(type)}`}
                name="action_plan_types"
                label={capitalize(type)}
                type="checkbox"
                value={capitalize(type)}
                component={CheckboxAdapter}
              />
            ))}
          </FormGroup>
        </Col>
        <Col xs={4}>
          <ActionPlanStatusField
            label="Action Plan Status"
            name="action_plan_status"
          />
        </Col>
      </Row>

      <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
        <h3>Description</h3>
      </div>

      <Row>
        <Col xs={12} className="m-b-24" style={{ zIndex: '3' }}>
          <Field name="currentState">
            {({ name, input }) => {
              return (
                <Editor
                  name={name}
                  data={validateEditorData(input.value)}
                  label="Current State"
                  setEditorInstanceForParent={setCurrentStateEditorInstance}
                  options={{
                    holder: 'current-state-change-impact-editor',
                  }}
                />
              );
            }}
          </Field>
        </Col>
        <Col xs={12} className="m-b-24" style={{ zIndex: '2' }}>
          <Field name="futureState">
            {({ name, input }) => {
              return (
                <Editor
                  name={name}
                  data={validateEditorData(input.value)}
                  label="Future State"
                  setEditorInstanceForParent={setFutureStateEditorInstance}
                  options={{
                    holder: 'future-state-change-impact-editor',
                  }}
                />
              );
            }}
          </Field>
        </Col>
        <Col xs={12} className="m-b-24" style={{ zIndex: '1' }}>
          <Field name="actionPlan">
            {({ name, input }) => {
              return (
                <Editor
                  name={name}
                  data={validateEditorData(input.value)}
                  label="Action Plan"
                  setEditorInstanceForParent={setActionPlanEditorInstance}
                  options={{
                    holder: 'action-plan-change-impact-editor',
                  }}
                />
              );
            }}
          </Field>
        </Col>
      </Row>

      <Row className="m-b-48">
        {submitFailed && (
          <FormGroup validationState="error">
            <HelpBlock>
              {submitError || 'Please fill in all required fields'}
            </HelpBlock>
          </FormGroup>
        )}
        <Col xs={12}>
          <div className="flex">
            <div className="flex">
              <ChangeImpactsLink projectId={projectId}>
                <Button>Cancel</Button>
              </ChangeImpactsLink>
              <Button className="m-l-12" bsStyle="primary" type="submit">
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>
      <InfoModal
        show={showLimitInfoModal}
        onHide={() => setShowLimitInfoModal(false)}
        title="Custom Change Impact Types Limit Reached"
        message={
          <React.Fragment>
            <p className="text-muted">
              You’ve reached the limit for custom change impact types in
              account. You can optimize types list via renaming or deleting
              type(s).
            </p>
            <p className="text-muted">
              If you would like to add more capabilities to your account, please
              contact your ChangeSync Account Manager.
            </p>
          </React.Fragment>
        }
      />
    </form>
  );
}

export default ChangeImpactForm;
